import { useMemo } from "react";

import { useList } from "@refinedev/core";

import { PractitionerRole } from "@/fhir/types";
import { FIVE_MINUTE_STALE_TIME } from "@/providers/data/fhir-data-provider";
import { FhirResourceType, formatPractitionerName, formatters } from "@/utils/fhir-utils";

import { fhirR4 } from "@smile-cdr/fhirts";

export interface OrganizationPractitioner {
  id: string;
  name: string;
  reference: fhirR4.Reference;
}

export interface UseOrganizationPractitionersResult {
  practitioners: OrganizationPractitioner[];
  practitionerRoles: fhirR4.PractitionerRole[];
  practitionerResources: fhirR4.Practitioner[];
  isLoading: boolean;
  refetch: () => Promise<unknown>;
}

interface UseOrganizationPractitionersOptions {
  includeInactive?: boolean;
}

export const useOrganizationPractitioners = (
  organizationReference: string | undefined,
  options?: UseOrganizationPractitionersOptions
): UseOrganizationPractitionersResult => {
  const { includeInactive = false } = options ?? {};

  const {
    result,
    query: { isLoading: isListLoading, refetch },
  } = useList<PractitionerRole>({
    resource: FhirResourceType.PractitionerRole,
    filters: organizationReference
      ? [
          { field: "organization", operator: "eq", value: organizationReference },
          ...(!includeInactive ? [{ field: "active", operator: "eq" as const, value: true as const }] : []),
        ]
      : [],
    meta: {
      customQueryParameters: ["_include=PractitionerRole:practitioner"],
      fetchAllPages: true,
    },
    queryOptions: {
      enabled: !!organizationReference,
      staleTime: FIVE_MINUTE_STALE_TIME,
    },
  });

  const { practitioners, practitionerRoles, practitionerResources } = useMemo(() => {
    const allResources = (result?.data as unknown as fhirR4.Resource[] | undefined) ?? [];

    // Separate PractitionerRole and Practitioner resources
    const rolesById = new Map<string, fhirR4.PractitionerRole>();
    const practitionersById = new Map<string, fhirR4.Practitioner>();

    for (const r of allResources) {
      if (!r) {
        continue;
      }
      if (r.resourceType === FhirResourceType.PractitionerRole) {
        const pr = r as fhirR4.PractitionerRole;
        if (pr.id) {
          rolesById.set(pr.id, pr);
        }
      } else if (r.resourceType === FhirResourceType.Practitioner) {
        const p = r as fhirR4.Practitioner;
        if (p.id) {
          practitionersById.set(p.id, p);
        }
      }
    }

    // Build unique practitioner options for dropdowns
    const unique: OrganizationPractitioner[] = [];
    for (const p of practitionersById.values()) {
      const name = formatPractitionerName(p);
      const ref = formatters.formatPractitioner(p.id!);
      unique.push({
        id: p.id!,
        name,
        reference: { reference: ref, display: name },
      });
    }
    unique.sort((a, b) => a.name.localeCompare(b.name));

    return {
      practitioners: unique,
      practitionerRoles: Array.from(rolesById.values()),
      practitionerResources: Array.from(practitionersById.values()),
    };
  }, [result?.data]);

  return {
    practitioners,
    practitionerRoles,
    practitionerResources,
    isLoading: isListLoading,
    refetch,
  };
};
